/**
 * HTTP client for the Manage Team API (/manage-team-api/*).
 * All calls after auth carry the session JWT as a Bearer token.
 */

export interface MtSession {
  sessionToken: string;
  userId: string;
  orgId: string;
  productId: string;
  productName: string;
  canWrite: boolean;
}

export interface MtMember {
  userId: string;
  displayName: string;
  email: string | null;
  imageUrl: string | null;
  orgRole: string | null;
  toolRole: string;
  toolRoleSource: string;
}

export interface MtGroup {
  id: string;
  name: string;
  description: string | null;
  productRole: string | null;
  memberCount: number;
  orgId: string;
  createdBy: string;
  createdAt: string;
}

export interface MtGroupMember {
  clerkUserId: string;
  displayName: string;
  assignedBy: string;
  assignedAt: string;
}

export interface MtCrossOrgRule {
  id: string;
  fromOrgId: string;
  fromOrgName: string | null;
  toOrgId: string | null;
  toOrgName: string | null;
  status: "active" | "pending" | "rejected";
  adminMapsTo: string;
  managerMapsTo: string;
  memberMapsTo: string;
  productId: string | null;
  requestedBy: string | null;
  createdAt: string;
}

const BASE = "";

async function apiFetch(
  sessionToken: string,
  path: string,
  opts: RequestInit = {}
): Promise<unknown> {
  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionToken}`,
      ...(opts.headers ?? {}),
    },
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: string; code?: string };
      msg = body.error ?? msg;
    } catch {
      // ignore
    }
    throw new Error(msg);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export async function exchangeToken(token: string): Promise<MtSession> {
  const res = await fetch("/manage-team-api/auth", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    let msg = "Token exchange failed";
    try {
      const body = (await res.json()) as { error?: string };
      msg = body.error ?? msg;
    } catch {
      // ignore
    }
    throw new Error(msg);
  }
  return res.json() as Promise<MtSession>;
}

// ---------------------------------------------------------------------------
// Members
// ---------------------------------------------------------------------------

export async function listMembers(
  session: MtSession
): Promise<{ members: MtMember[] }> {
  return apiFetch(
    session.sessionToken,
    `/manage-team-api/${session.productId}/members`
  ) as Promise<{ members: MtMember[] }>;
}

export async function grantRole(
  session: MtSession,
  clerkUserId: string,
  role: string
): Promise<void> {
  await apiFetch(
    session.sessionToken,
    `/manage-team-api/${session.productId}/members/grant`,
    { method: "POST", body: JSON.stringify({ clerkUserId, role }) }
  );
}

export async function revokeRole(
  session: MtSession,
  clerkUserId: string
): Promise<void> {
  await apiFetch(
    session.sessionToken,
    `/manage-team-api/${session.productId}/members/grant`,
    { method: "DELETE", body: JSON.stringify({ clerkUserId }) }
  );
}

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------

export async function listGroups(
  session: MtSession
): Promise<{ groups: MtGroup[] }> {
  return apiFetch(
    session.sessionToken,
    `/manage-team-api/${session.productId}/groups`
  ) as Promise<{ groups: MtGroup[] }>;
}

export async function createGroup(
  session: MtSession,
  name: string,
  description?: string
): Promise<{ group: MtGroup }> {
  return apiFetch(
    session.sessionToken,
    `/manage-team-api/${session.productId}/groups`,
    { method: "POST", body: JSON.stringify({ name, description }) }
  ) as Promise<{ group: MtGroup }>;
}

export async function updateGroup(
  session: MtSession,
  groupId: string,
  updates: { name?: string; description?: string | null; productRole?: string | null }
): Promise<void> {
  await apiFetch(
    session.sessionToken,
    `/manage-team-api/${session.productId}/groups/${groupId}`,
    { method: "PUT", body: JSON.stringify(updates) }
  );
}

export async function deleteGroup(
  session: MtSession,
  groupId: string
): Promise<void> {
  await apiFetch(
    session.sessionToken,
    `/manage-team-api/${session.productId}/groups/${groupId}`,
    { method: "DELETE" }
  );
}

export async function listGroupMembers(
  session: MtSession,
  groupId: string
): Promise<{ members: MtGroupMember[] }> {
  return apiFetch(
    session.sessionToken,
    `/manage-team-api/${session.productId}/groups/${groupId}/members`
  ) as Promise<{ members: MtGroupMember[] }>;
}

export async function addGroupMember(
  session: MtSession,
  groupId: string,
  clerkUserId: string
): Promise<void> {
  await apiFetch(
    session.sessionToken,
    `/manage-team-api/${session.productId}/groups/${groupId}/members`,
    { method: "POST", body: JSON.stringify({ clerkUserId }) }
  );
}

export async function removeGroupMember(
  session: MtSession,
  groupId: string,
  clerkUserId: string
): Promise<void> {
  await apiFetch(
    session.sessionToken,
    `/manage-team-api/${session.productId}/groups/${groupId}/members/${clerkUserId}`,
    { method: "DELETE" }
  );
}

// ---------------------------------------------------------------------------
// Cross-org
// ---------------------------------------------------------------------------

export async function listCrossOrg(
  session: MtSession
): Promise<{ rules: MtCrossOrgRule[] }> {
  return apiFetch(
    session.sessionToken,
    `/manage-team-api/${session.productId}/cross-org`
  ) as Promise<{ rules: MtCrossOrgRule[] }>;
}

export async function createCrossOrgRule(
  session: MtSession,
  data: {
    toOrgId?: string | null;
    toOrgName?: string | null;
    fromOrgName?: string | null;
    adminMapsTo?: string;
    managerMapsTo?: string;
    memberMapsTo?: string;
  }
): Promise<{ rule: MtCrossOrgRule }> {
  return apiFetch(
    session.sessionToken,
    `/manage-team-api/${session.productId}/cross-org`,
    { method: "POST", body: JSON.stringify(data) }
  ) as Promise<{ rule: MtCrossOrgRule }>;
}

export async function deleteCrossOrgRule(
  session: MtSession,
  ruleId: string
): Promise<void> {
  await apiFetch(
    session.sessionToken,
    `/manage-team-api/${session.productId}/cross-org/${ruleId}`,
    { method: "DELETE" }
  );
}
